import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Paper,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Chip,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Editor } from "@monaco-editor/react";
import { useAlert } from "../../context/AlertContext";
import { Assertion, AssertionRequest } from "../../types";
import api from "../../services/api";
import { useThemeMode } from "context/ThemeContext";

interface AssertionFormProps {
  initialData?: Assertion;
  isEdit?: boolean;
  isModal?: boolean;
  onCancel?: () => void;
  onSubmit?: (values: AssertionRequest) => Promise<void>;
}

// Available assertion types
const assertionTypes = [
  {
    value: "equals",
    label: "Equals",
    description: "Check if output equals a specific string",
  },
  {
    value: "contains",
    label: "Contains",
    description: "Check if output contains a specific string",
  },
  {
    value: "matches",
    label: "Matches",
    description: "Check if output matches a regular expression",
  },
  {
    value: "similar",
    label: "Similar",
    description: "Check if output is semantically similar to a reference",
  },
  {
    value: "is-json",
    label: "Is Valid JSON",
    description: "Check if output is valid JSON matching a schema",
  },
  {
    value: "llm-rubric",
    label: "LLM Rubric",
    description: "Use an LLM to grade the output based on a rubric",
  },
  {
    value: "javascript",
    label: "JavaScript",
    description: "Evaluate output using a JavaScript function",
  },
];

const AssertionForm: React.FC<AssertionFormProps> = ({
  initialData,
  isEdit = false,
  isModal = false,
  onCancel,
  onSubmit,
}) => {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const alert = useAlert();
  const [loading, setLoading] = useState<boolean>(false);

  // Add state for JSON validation
  const [jsonSchemaValid, setJsonSchemaValid] = useState<boolean>(true);
  const [jsonSchemaError, setJsonSchemaError] = useState<string | null>(null);

  // Initialize JSON validation on mount
  useEffect(() => {
    if (initialData && initialData.type === "is-json") {
      try {
        JSON.parse(initialData.value);
        setJsonSchemaValid(true);
        setJsonSchemaError(null);
      } catch (error) {
        setJsonSchemaValid(false);
        setJsonSchemaError((error as Error).message);
      }
    }
  }, [initialData]);

  // Define validation schema
  const validationSchema = Yup.object({
    type: Yup.string().required("Assertion type is required"),
    value: Yup.string().required("Assertion value is required"),
    description: Yup.string(),
  });

  // Initialize formik
  const formik = useFormik<AssertionRequest>({
    initialValues: {
      type: initialData?.type || "equals",
      value: initialData?.value || "",
      description: initialData?.description || "",
    },
    validationSchema,
    onSubmit: async (values) => {
      // Validate JSON schema if applicable
      if (values.type === "is-json" && !jsonSchemaValid) {
        alert.error("Please provide a valid JSON schema");
        return;
      }

      setLoading(true);
      try {
        // If custom submission handler is provided, use it
        if (onSubmit) {
          await onSubmit(values);
        } else {
          // Otherwise use the default API calls
          if (isEdit && initialData?._id) {
            await api.assertions.update(initialData._id, values);
            alert.success("Assertion updated successfully");
          } else {
            await api.assertions.create(values);
            alert.success("Assertion created successfully");
          }

          if (isModal && onCancel) {
            onCancel();
          } else {
            navigate("/assertions");
          }
        }
      } catch (error) {
        console.error("Error saving assertion:", error);
        alert.error("Failed to save assertion");
      } finally {
        setLoading(false);
      }
    },
  });

  // Check JSON validity on mount and when type/value changes
  useEffect(() => {
    if (formik.values.type === "is-json") {
      try {
        if (formik.values.value.trim() === "") {
          // Set default value for JSON schema if empty
          const defaultSchema =
            '{\n  "type": "object",\n  "properties": {\n    "example": {\n      "type": "string"\n    }\n  }\n}';
          formik.setFieldValue("value", defaultSchema);
          setJsonSchemaValid(true);
          setJsonSchemaError(null);
        } else {
          JSON.parse(formik.values.value);
          setJsonSchemaValid(true);
          setJsonSchemaError(null);
        }
      } catch (error) {
        setJsonSchemaValid(false);
        setJsonSchemaError((error as Error).message);
      }
    }
  }, [formik.values.type]); // Only run when type changes, not when value changes

  // Handle JSON validation when the value changes
  const handleEditorChange = (value: string | undefined) => {
    const newValue = value || "";
    formik.setFieldValue("value", newValue);

    if (formik.values.type === "is-json") {
      try {
        if (newValue.trim() === "") {
          setJsonSchemaValid(false);
          setJsonSchemaError("JSON schema cannot be empty");
        } else {
          JSON.parse(newValue);
          setJsonSchemaValid(true);
          setJsonSchemaError(null);
        }
      } catch (error) {
        setJsonSchemaValid(false);
        setJsonSchemaError((error as Error).message);
      }
    }
  };

  // Get editor language based on assertion type
  const getEditorLanguage = (): string => {
    switch (formik.values.type) {
      case "matches":
        return "plaintext"; // for regex
      case "is-json":
        return "json";
      case "javascript":
        return "javascript";
      default:
        return "plaintext";
    }
  };

  // Get placeholder for editor based on assertion type
  const getEditorPlaceholder = (): string => {
    switch (formik.values.type) {
      case "equals":
        return "Exact text the output should match";
      case "contains":
        return "Text that should be contained in the output";
      case "matches":
        return "^Regular expression to match$";
      case "similar":
        return "Reference text to compare similarity against";
      case "is-json":
        return '{\n  "type": "object",\n  "properties": {\n    "example": {\n      "type": "string"\n    }\n  }\n}';
      case "llm-rubric":
        return "Instructions for LLM to evaluate the output.\nExample: Check if the response answers the user question directly and contains accurate information.";
      case "javascript":
        return 'function assert(output, { test }) {\n  // Your assertion logic here\n  return output.includes("example");\n}';
      default:
        return "";
    }
  };

  // Get description text based on assertion type
  const getTypeDescription = (): string => {
    const selectedType = assertionTypes.find(
      (type) => type.value === formik.values.type,
    );
    return selectedType?.description || "";
  };

  return (
    <Box component="form" onSubmit={formik.handleSubmit} noValidate>
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Assertion Details
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <FormControl
          fullWidth
          sx={{ mb: 3 }}
          error={formik.touched.type && Boolean(formik.errors.type)}
        >
          <InputLabel id="assertion-type-label">Assertion Type</InputLabel>
          <Select
            labelId="assertion-type-label"
            name="type"
            value={formik.values.type}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Assertion Type"
          >
            {assertionTypes.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
          {formik.touched.type && formik.errors.type ? (
            <FormHelperText>{formik.errors.type}</FormHelperText>
          ) : (
            <FormHelperText>{getTypeDescription()}</FormHelperText>
          )}
        </FormControl>

        <TextField
          fullWidth
          label="Description"
          name="description"
          value={formik.values.description}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.description && Boolean(formik.errors.description)
          }
          helperText={formik.touched.description && formik.errors.description}
          variant="outlined"
          multiline
          rows={2}
          sx={{ mb: 3 }}
        />
      </Paper>

      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography variant="h6">Assertion Value</Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />

        {formik.values.type === "is-json" ? (
          <>
            <Box sx={{ mt: 1, mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                This assertion validates that the response is valid JSON and
                conforms to the specified schema.
              </Typography>
            </Box>
            <Box
              sx={{
                mt: 2,
                mb: 2,
                border: (theme) =>
                  `1px solid ${
                    jsonSchemaValid
                      ? theme.palette.divider
                      : theme.palette.error.main
                  }`,
                borderRadius: 1,
                height: 300,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  pl: 2,
                  pr: 2,
                  pt: 1,
                  pb: 0.5,
                  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(0, 0, 0, 0.02)",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: "medium",
                  }}
                >
                  JSON Schema
                </Typography>
                {jsonSchemaValid ? (
                  <Chip
                    label="Valid JSON"
                    color="success"
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: "medium" }}
                  />
                ) : (
                  <Chip
                    label="Invalid JSON"
                    color="error"
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: "medium" }}
                  />
                )}
              </Box>
              <Editor
                language={getEditorLanguage()}
                value={formik.values.value}
                onChange={handleEditorChange}
                theme={mode === "dark" ? "vs-dark" : "light"}
                options={{
                  minimap: { enabled: false },
                  fontSize: 14,
                  wordWrap: "on",
                  lineNumbers: "on",
                  folding: true,
                  lineDecorationsWidth: 10,
                  lineNumbersMinChars: 3,
                }}
              />
            </Box>
            {jsonSchemaError && (
              <Typography
                variant="caption"
                color="error"
                sx={{
                  display: "block",
                  backgroundColor: "rgba(244, 67, 54, 0.08)",
                  fontWeight: "medium",
                  padding: 1.5,
                  borderRadius: 1,
                }}
              >
                {jsonSchemaError}
              </Typography>
            )}
          </>
        ) : (
          <Box
            sx={{
              height: 300,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              mb: 2,
            }}
          >
            <Editor
              language={getEditorLanguage()}
              value={formik.values.value}
              onChange={handleEditorChange}
              theme={mode === "dark" ? "vs-dark" : "light"}
              options={{
                minimap: { enabled: false },
                fontSize: 14,
                wordWrap: "on",
                lineNumbers: "on",
                folding: true,
                lineDecorationsWidth: 10,
                lineNumbersMinChars: 3,
              }}
            />
          </Box>
        )}
        {formik.touched.value && formik.errors.value && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {formik.errors.value}
          </Typography>
        )}
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={
            isModal && onCancel ? onCancel : () => navigate("/assertions")
          }
        >
          Cancel
        </Button>
        <LoadingButton
          type="submit"
          variant="contained"
          color="primary"
          loading={loading}
        >
          {isEdit ? "Update Assertion" : "Create Assertion"}
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default AssertionForm;
